if (!customElements.get('card-product-slider')) {
  class CardProductSlider extends HTMLElement {
    constructor() {
      super();

      const swiperOptions =
        JSON.parse(this.getAttribute('data-swiper-options')) || {};

      this.sliderWrapper = this.querySelector('.swiper-wrapper');
      this.requiresFetch = this.hasAttribute('data-url');
      this.parent = this.closest('[data-section-id]');
      this.sliderOptions = {
        a11y: false,
        // Higher than browser touch-slop so a vertical page scroll that
        // begins on a product image is not claimed as a horizontal swipe.
        threshold: 12,
        // Swiper default true calls preventDefault on touchstart, which
        // blocks vertical scrolling when the first touch lands on a card.
        touchStartPreventDefault: false,
        // Prefer true vertical pans; only lock to horizontal once the
        // gesture is clearly sideways (default is 45°).
        touchAngle: 30,
        spaceBetween: swiperOptions.spaceBetweenMobile || 2,
        slidesPerView: swiperOptions.slidesPerViewMobile || 'auto',
        resistanceRatio: 0.72,
        breakpoints: {
          750: {
            slidesPerView: 3,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          },
          1100: {
            slidesPerView: 4,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          }
        }
      };
      if (swiperOptions.navigation) {
        this.sliderOptions.navigation = {
          ...swiperOptions.navigation
        };
      }
      if (swiperOptions.autoplay) {
        this.sliderOptions.autoplay = {
          ...swiperOptions.autoplay
        };
      }
      if (swiperOptions.loop) {
        this.sliderOptions.loop = swiperOptions.loop;
      }
    }

    connectedCallback() {
      if (this.requiresFetch) {
        this.fetchItems();
        return;
      }
      this.initSlider();
    }

    fetchItems() {
      if (!this.parent) throw new Error('Parent is missing');
      fetch(this.getAttribute('data-url'))
        .then(response => response.text())
        .then(text => {
          const html = new DOMParser().parseFromString(
            text,
            'text/html'
          );
          const wrapper = html.querySelector('.swiper-wrapper');

          if (wrapper && wrapper.innerHTML.trim().length) {
            this.querySelector('.swiper-wrapper').innerHTML =
              wrapper.innerHTML;
            this.parent.classList.remove('hidden');
            this.initSlider();
          }
        })
        .catch(e => {
          console.error(e);
        });
    }

    initSlider(slider = this) {
      this.slider = new Swiper(slider, this.sliderOptions);

      const mediaAreaSlideshowNavVisibleClass =
        'product-card__media-area--slideshow-nav-visible';
      const carouselActiveAttr = 'data-slideshow-nav-carousel-active';
      const sliderRoot = this;
      // true only while this product-row intersects the vertical center band
      // of the viewport (see IntersectionObserver below). Not the same as
      // Swiper's horizontal activeIndex.
      let rowInVerticalCenter = false;
      // true while the user is actively dragging/swiping this row
      let rowUserSliding = false;

      function isMobileCompactSlideshowViewport() {
        return (
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(max-width: 749px)').matches
        );
      }

      function clearCarouselActiveSlideshowNav() {
        sliderRoot
          .querySelectorAll('[' + carouselActiveAttr + ']')
          .forEach(area => {
            area.removeAttribute(carouselActiveAttr);
            area.classList.remove(mediaAreaSlideshowNavVisibleClass);
          });
      }

      /**
       * Mobile: show in-card slideshow arrows on the horizontally active card
       * only when the user is sliding this row OR the row is vertically in the
       * center of the viewport. Leaving center (and not sliding) hides them.
       * Drag-revealed nav without the carousel-active attr is left alone.
       */
      function syncSlideshowNavToActiveCarouselSlide() {
        clearCarouselActiveSlideshowNav();

        if (!isMobileCompactSlideshowViewport()) {
          return;
        }

        if (!rowInVerticalCenter && !rowUserSliding) {
          return;
        }

        const activeSlide =
          sliderRoot.slider.slides[sliderRoot.slider.activeIndex];
        if (!activeSlide) {
          return;
        }

        const mediaArea = activeSlide.querySelector('.product-card__media-area');
        const slideshowMedia = activeSlide.querySelector(
          '.product-card__media.product-card__slideshow'
        );
        if (!mediaArea || !slideshowMedia) {
          return;
        }

        const slideImages = slideshowMedia.querySelectorAll(
          'img.product-card__slide'
        );
        if (slideImages.length < 2) {
          return;
        }

        mediaArea.setAttribute(carouselActiveAttr, '');
        mediaArea.classList.add(mediaAreaSlideshowNavVisibleClass);
      }

      this.slider.on('slideChange', syncSlideshowNavToActiveCarouselSlide);
      this.slider.on('transitionEnd', syncSlideshowNavToActiveCarouselSlide);

      // Reveal only once the finger actually moves (not on bare touch/tap).
      this.slider.on('sliderFirstMove', () => {
        if (!isMobileCompactSlideshowViewport()) {
          return;
        }
        rowUserSliding = true;
        syncSlideshowNavToActiveCarouselSlide();
      });
      this.slider.on('touchEnd', () => {
        if (!rowUserSliding) {
          return;
        }
        rowUserSliding = false;
        // After a swipe, only keep arrows if the row is still in vertical center
        syncSlideshowNavToActiveCarouselSlide();
      });

      // Middle ~30% of the viewport: row must intersect this band to count as
      // "vertically centered". rootMargin shrinks the observer root from top
      // and bottom (percentage margins are relative to the root size).
      if (typeof IntersectionObserver === 'function') {
        const centerBandObserver = new IntersectionObserver(
          entries => {
            const entry = entries[0];
            if (!entry) {
              return;
            }
            rowInVerticalCenter = entry.isIntersecting;
            syncSlideshowNavToActiveCarouselSlide();
          },
          {
            root: null,
            rootMargin: '-35% 0px -35% 0px',
            threshold: 0
          }
        );
        centerBandObserver.observe(sliderRoot);
      } else {
        // Fallback: treat as in-view so slideChange still works without IO
        rowInVerticalCenter = true;
        syncSlideshowNavToActiveCarouselSlide();
      }

      const mq749 = window.matchMedia('(max-width: 749px)');
      function onMq749Change() {
        if (!mq749.matches) {
          rowUserSliding = false;
          rowInVerticalCenter = false;
          clearCarouselActiveSlideshowNav();
          return;
        }
        syncSlideshowNavToActiveCarouselSlide();
      }
      if (typeof mq749.addEventListener === 'function') {
        mq749.addEventListener('change', onMq749Change);
      } else if (typeof mq749.addListener === 'function') {
        mq749.addListener(onMq749Change);
      }

      this.slider.wrapperEl.setAttribute('aria-live', 'polite');
      this.slider.wrapperEl.id = `swiper-wrapper-${this.dataset.sectionId}`;
      this.slider.slides.forEach((slide, i) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute(
          'aria-label',
          `${i + 1} / ${this.slider.slides.length}`
        );
        slide.dataset.index = i;
      });

      if (this.swiperOptions?.navigation) {
        const sliderButtons = [
          this.slider.navigation.prevEl,
          this.slider.navigation.nextEl
        ];

        sliderButtons.forEach((button, i) => {
          let type = 'Previous';

          if (i === 1) {
            type = 'Next';
          }

          button.setAttribute('aria-label', `${type} slide`);
          button.setAttribute(
            'aria-controls',
            this.slider.wrapperEl.id
          );
        });
      }

      this.slider.wrapperEl.addEventListener('focusin', e => {
        if (e.target.closest('.product-card__slideshow-nav')) {
          return;
        }

        /**
         * Desktop keyboard a11y: keep focused slide scrolled into view.
         * Mobile (touch horizontal swipe + in-card slideshow): focusin fires from taps/buttons and
         * this logic fights Swiper translation — carousel "jumps back" after using product images then swiping.
         */
        if (
          typeof window.matchMedia === 'function' &&
          !window.matchMedia('(min-width: 750px)').matches
        ) {
          return;
        }

        this.slider.el.scrollLeft = 0;

        const slide = e.target.closest('.swiper-slide');
        const slideIndex = +slide.dataset.index;
        const isFirstSlide = slideIndex === 0;
        const isLastSlide =
          slideIndex === this.slider.slides.length - 1;

        if (isFirstSlide || isLastSlide) {
          this.slider.slideTo(slideIndex, 0);

          return;
        }

        const slideRect = slide.getBoundingClientRect();
        const wrapperWidth = this.slider.wrapperEl.clientWidth;
        // prettier-ignore
        const slideLeft = slideRect.x + slideRect.width - ((document.body.clientWidth - wrapperWidth) / 2);
        const isSlideVisible =
          slideLeft <= wrapperWidth && slideLeft >= slideRect.width;

        if (isSlideVisible) {
          return;
        }

        slideLeft >= slideRect.width
          ? this.slider.slideNext(0)
          : this.slider.slidePrev(0);
      });

      const swiperOptions =
        JSON.parse(this.getAttribute('data-swiper-options')) || {};
      /** stop / start autoplay on hover if quick cart is not open */
      this.slider.wrapperEl.addEventListener('mouseenter', () =>
        this.slider.autoplay.stop()
      );
      this.slider.wrapperEl.addEventListener('mouseleave', () => {
        if (!this.classList.contains('product--open-on-quick-cart')) {
          this.slider.autoplay.start();
        }
        if (swiperOptions.autoplay === false) {
          this.slider.autoplay.stop();
        }
      });

      if (
        this.hasAttribute('data-init-quick-cart') &&
        document.querySelector('quick-cart-drawer')
      ) {
        document.querySelector('quick-cart-drawer').init();
      }

      this.querySelectorAll('product-card').forEach(card => {
        card.init();
      });

      if (Shopify.designMode) {
        if (
          this.hasAttribute('data-init-quick-cart') &&
          document.querySelector('quick-cart-drawer')
        ) {
          document.querySelector('quick-cart-drawer').init();
        }
      }
    }
  }

  customElements.define('card-product-slider', CardProductSlider);
}
